
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { saveAs } from "file-saver";

import { getSocket } from "../ChatPage/socket";
import Layout from "../../components/Layout";
import Loader from "../../components/Loader";
import {
    EMPTY_PRINT_DRAFT,
    buildPrintCatalog,
    createPrintCatalog,
    deletePrintCatalog,
    deletePrintCatalogImage,
    fetchPrintCatalogFile,
    fetchPrintCatalogImage,
    getPrintCatalog,
    getPrintCatalogCategories,
    getPrintCatalogTags,
    listPrintCatalogs,
    previewPrintCatalog,
    readApiError,
    toPrintDraft,
    updatePrintCatalog,
    uploadPrintCatalogImage,
} from "./printCatalogApi";

/*
 * Каталоги-журналы под печать (PDF).
 *
 * Слева — список каталогов, посередине — настройки выбранного, справа — просмотр:
 * пробник (обложка, две страницы и контакты по тому, что сейчас на экране) или
 * любой из собранных PDF. Сам PDF собирает сервер в фоне.
 */

/** Пока каталог собирается, спрашиваем сервер о ходе сборки с таким шагом. */
const POLL_MS = 1500;
/** Пауза после последней правки, прежде чем пробник обновится сам. */
const AUTO_PREVIEW_MS = 1200;
/** Сколько подсказок тегов показываем разом. */
const TAG_SUGGESTIONS = 40;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;

const ACCENT_COLORS = ["#1f3b34", "#1e293b", "#1e3a5f", "#3b2f2f", "#7c2d12", "#4c1d95", "#111111"];

const PER_PAGE = [
    { value: 4, label: "4", hint: "2 × 2, крупно" },
    { value: 6, label: "6", hint: "2 в ряд" },
    { value: 9, label: "9", hint: "3 в ряд" },
    { value: 3, label: "В ряд", hint: "товар и 3 его фото" },
];

const ROWS_PER_PAGE = [
    { value: 1, label: "1", hint: "на весь лист" },
    { value: 2, label: "2", hint: "крупно" },
    { value: 3, label: "3", hint: "фото в ряд" },
    { value: 4, label: "4", hint: "плотно" },
];

const DESIGNS = [
    { value: "classic", label: "Классика", hint: "рамки и плашки" },
    { value: "minimal", label: "Строгое", hint: "без рамок" },
    { value: "dark", label: "Тёмное", hint: "золото на тёмном" },
];

const IMAGE_CROPS = [
    { value: "none", label: "Как есть", hint: "16:9, целиком" },
    { value: "square", label: "Квадрат 1:1", hint: "бока обрезаются" },
];

const PRICE_MODES = [
    { value: "public", label: "Розница" },
    { value: "subscriber", label: "Опт" },
    { value: "milling", label: "Фрезеровка" },
    { value: "none", label: "Без цен" },
];

const NUMBERINGS = [
    { value: "sequence", label: "По порядку" },
    { value: "article", label: "Артикул" },
    { value: "model", label: "Номер формы" },
];

const SORTS = [
    { value: "name", label: "Название" },
    { value: "article", label: "Артикул" },
    { value: "price", label: "Цена" },
    { value: "new", label: "Новые" },
];

const COVER_MODES = [
    { value: "auto", label: "Первый товар" },
    { value: "product", label: "Товар по артикулу" },
    { value: "photo", label: "Своё фото" },
];

const TAG_MATCHES = [
    { value: "any", label: "С любым из тегов" },
    { value: "all", label: "Со всеми сразу" },
];

const MARK_SOURCES = [
    { value: "text", label: "Название" },
    { value: "logo", label: "Логотип" },
];

const PHOTO_MARKS = [
    { value: "none", label: "Нет", hint: "фото чистые" },
    { value: "corner", label: "В углу", hint: "аккуратно" },
    { value: "tile", label: "Сеткой", hint: "по всему фото" },
];

const MARK_STRENGTHS = [
    { value: "light", label: "Слабый" },
    { value: "normal", label: "Средний" },
    { value: "strong", label: "Сильный" },
];

const SWITCHES = [
    ["showQr", "QR-код на каждой карточке"],
    ["showCover", "Обложка"],
    ["showContacts", "Страница контактов в конце"],
    ["groupByCategory", "Каждая категория — свой раздел"],
];

// Какие товары брать по готовности: каждое включённое условие сужает отбор.
const READY_SWITCHES = [
    ["onlyWithPrice", "С ценой (той, что печатается)"],
    ["onlyWithMilling", "С ценой фрезеровки"],
    ["onlyTransferred", "Проверены и перенесены из каталога форм"],
];

const PRICE_LABELS = { none: "без цен", public: "розница", subscriber: "опт", milling: "фрезеровка" };

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatBytes = (bytes) => {
    if (!(bytes > 0)) return "";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`;
};

/** Номер товара из поля ввода: только цифры, пусто — 0 («без границы»). */
const toPosition = (text) => {
    const number = Number(String(text).replace(/\D/g, "").slice(0, 4));
    return Number.isFinite(number) && number > 0 ? number : 0;
};

const sameDraft = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const hasContent = (draft, catalog) => (
    draft.categoryIds.length > 0 || draft.tags.length > 0 || (catalog?.productIds || []).length > 0
);

const statusInfo = (catalog) => {
    const status = catalog.build?.status;
    if (status === "running") return { text: "Собирается…", tone: "warning" };
    if (status === "error") return { text: "Ошибка сборки", tone: "danger" };
    if (catalog.files.length) return { text: "PDF готов", tone: "success" };
    return { text: "Не собран", tone: "muted" };
};

const layoutLabel = (options) => (
    options.perPage === 3 ? `в ряд, ${options.rowsPerPage} на странице` : `${options.perPage} на странице`
);

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
    reader.readAsDataURL(file);
});

const Choice = ({ options, value, onChange, disabled }) => (
    <div className="printCatalogs__choices">
        {options.map((option) => (
            <button
                key={String(option.value)}
                type="button"
                className={`printCatalogs__choice${option.value === value ? " is-active" : ""}`}
                disabled={disabled}
                onClick={() => onChange(option.value)}
            >
                <strong>{option.label}</strong>
                {option.hint ? <span>{option.hint}</span> : null}
            </button>
        ))}
    </div>
);

const Field = ({ label, value, onChange, placeholder, multiline, maxLength, disabled, type = "text" }) => (
    <label className="printCatalogs__field">
        <span>{label}</span>
        {multiline ? (
            <textarea
                value={value}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
                rows={3}
                onChange={(event) => onChange(event.target.value)}
            />
        ) : (
            <input
                type={type}
                value={value}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
            />
        )}
    </label>
);

const Section = ({ title, hint, children }) => (
    <section className="printCatalogs__section">
        <div className="printCatalogs__sectionHead">
            <h3>{title}</h3>
            {hint ? <p>{hint}</p> : null}
        </div>
        {children}
    </section>
);

/** Сворачиваемый блок: в свёрнутом виде видно только заголовок и что выбрано. */
const Collapse = ({ title, summary, open, onToggle, children }) => (
    <div className={`printCatalogs__collapse${open ? " is-open" : ""}`}>
        <button type="button" className="printCatalogs__collapseHead" aria-expanded={open} onClick={onToggle}>
            <span>
                <strong>{title}</strong>
                <em>{summary}</em>
            </span>
            <i aria-hidden="true">{open ? "▴" : "▾"}</i>
        </button>
        {open ? <div className="printCatalogs__collapseBody">{children}</div> : null}
    </div>
);

/** Загрузка картинки каталога (логотип или фото обложки) с показом текущей. */
const ImageUpload = ({ url, has, busy, disabled, wide, onPick, onRemove, emptyText }) => {
    const inputRef = useRef(null);

    return (
        <div className={`printCatalogs__upload${wide ? " is-wide" : ""}`}>
            <div className="printCatalogs__uploadBox">
                {has && url ? <img src={url} alt="" /> : <span>{has ? "Загрузка…" : emptyText}</span>}
            </div>
            <div className="printCatalogs__uploadActions">
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (file) onPick(file);
                    }}
                />
                <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()}>
                    {busy ? "Загрузка…" : has ? "Заменить" : "Загрузить"}
                </button>
                {has ? (
                    <button type="button" className="is-quiet" disabled={disabled} onClick={onRemove}>
                        Убрать
                    </button>
                ) : null}
            </div>
        </div>
    );
};

const PrintCatalogsPage = () => {
    const [catalogs, setCatalogs] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState("");

    /** Что открыто в настройках: '' — ничего, 'new' — новый каталог, иначе id. */
    const [selectedId, setSelectedId] = useState("");
    const [catalog, setCatalog] = useState(null);
    const [draft, setDraft] = useState(EMPTY_PRINT_DRAFT);
    const [catalogLoading, setCatalogLoading] = useState(false);

    const [categories, setCategories] = useState([]);
    const [allTags, setAllTags] = useState([]);
    const [tagSearch, setTagSearch] = useState("");
    /** Списки категорий и тегов длинные — свёрнуты, пока их не откроют. */
    const [categoriesOpen, setCategoriesOpen] = useState(false);
    const [tagsOpen, setTagsOpen] = useState(false);

    const [busy, setBusy] = useState("");
    const [message, setMessage] = useState(null);

    /** Что показано справа: пробник или собранный файл. */
    const [viewer, setViewer] = useState({ url: "", kind: "", fileId: "", info: null });
    const [viewerLoading, setViewerLoading] = useState(false);
    const [viewerError, setViewerError] = useState("");
    const [autoPreview, setAutoPreview] = useState(false);

    const [logoUrl, setLogoUrl] = useState("");
    const [coverUrl, setCoverUrl] = useState("");

    const mountedRef = useRef(true);
    const viewerUrlRef = useRef("");
    const previewSeqRef = useRef(0);

    useEffect(() => () => {
        mountedRef.current = false;
        if (viewerUrlRef.current) URL.revokeObjectURL(viewerUrlRef.current);
    }, []);

    const saved = useMemo(() => (catalog ? toPrintDraft(catalog) : EMPTY_PRINT_DRAFT), [catalog]);
    const dirty = selectedId !== "" && !sameDraft(draft, saved);
    const running = catalog?.build?.status === "running";
    const locked = !!busy || running;
    const draftKey = useMemo(() => JSON.stringify(draft), [draft]);

    const showViewer = useCallback((blob, next) => {
        if (viewerUrlRef.current) URL.revokeObjectURL(viewerUrlRef.current);
        const url = blob ? URL.createObjectURL(blob) : "";
        viewerUrlRef.current = url;
        setViewer({ url, kind: "", fileId: "", info: null, ...next });
    }, []);

    const loadList = useCallback(async (silent = false) => {
        if (!silent) setListLoading(true);
        try {
            const list = await listPrintCatalogs();
            if (!mountedRef.current) return;
            setCatalogs(list);
            setListError("");
        } catch (error) {
            if (!silent && mountedRef.current) setListError(await readApiError(error, "Не удалось загрузить каталоги"));
        } finally {
            if (mountedRef.current) setListLoading(false);
        }
    }, []);

    useEffect(() => {
        loadList();
        getPrintCatalogCategories()
            .then((list) => mountedRef.current && setCategories(
                [...list].sort((a, b) => Number(b.productCount || 0) - Number(a.productCount || 0))
            ))
            .catch(() => undefined);
        getPrintCatalogTags()
            .then((list) => mountedRef.current && setAllTags(list))
            .catch(() => undefined);
    }, [loadList]);

    /** Обновить один каталог в списке слева (после сохранения, сборки, события с сервера). */
    const mergeIntoList = useCallback((next) => {
        setCatalogs((current) => {
            const index = current.findIndex((item) => item._id === next._id);
            if (index < 0) return [next, ...current];
            const copy = [...current];
            copy[index] = { ...copy[index], ...next };
            return copy;
        });
    }, []);

    const applyCatalog = useCallback((next, withDraft) => {
        if (!mountedRef.current) return;
        setCatalog(next);
        if (withDraft) setDraft(toPrintDraft(next));
        mergeIntoList(next);
    }, [mergeIntoList]);

    const confirmLeave = useCallback(() => (
        !dirty || window.confirm("Есть несохранённые правки. Уйти без сохранения?")
    ), [dirty]);

    const openCatalog = useCallback(async (catalogId) => {
        if (catalogId === selectedId || !confirmLeave()) return;

        setSelectedId(catalogId);
        setMessage(null);
        setTagSearch("");
        setCategoriesOpen(false);
        setTagsOpen(false);
        setViewerError("");
        showViewer(null, {});
        setCatalog(null);
        setDraft(EMPTY_PRINT_DRAFT);
        if (!catalogId || catalogId === "new") return;

        setCatalogLoading(true);
        try {
            const next = await getPrintCatalog(catalogId);
            if (!mountedRef.current) return;
            applyCatalog(next, true);
        } catch (error) {
            if (!mountedRef.current) return;
            setMessage({ type: "error", text: await readApiError(error, "Каталог не открылся") });
            setSelectedId("");
        } finally {
            if (mountedRef.current) setCatalogLoading(false);
        }
    }, [applyCatalog, confirmLeave, selectedId, showViewer]);

    // Картинки логотипа и обложки закрыты входом — забираем их файлом и показываем с локального адреса.
    const logoVersion = catalog?.hasLogo ? `${catalog._id}:${catalog.logoUpdatedAt || ""}` : "";
    const coverVersion = catalog?.hasCover ? `${catalog._id}:${catalog.coverUpdatedAt || ""}` : "";

    useEffect(() => {
        if (!logoVersion) {
            setLogoUrl("");
            return undefined;
        }
        let alive = true;
        let url = "";
        fetchPrintCatalogImage(logoVersion.split(":")[0], "logo")
            .then((blob) => {
                if (!alive) return;
                url = URL.createObjectURL(blob);
                setLogoUrl(url);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
            if (url) URL.revokeObjectURL(url);
        };
    }, [logoVersion]);

    useEffect(() => {
        if (!coverVersion) {
            setCoverUrl("");
            return undefined;
        }
        let alive = true;
        let url = "";
        fetchPrintCatalogImage(coverVersion.split(":")[0], "cover")
            .then((blob) => {
                if (!alive) return;
                url = URL.createObjectURL(blob);
                setCoverUrl(url);
            })
            .catch(() => undefined);
        return () => {
            alive = false;
            if (url) URL.revokeObjectURL(url);
        };
    }, [coverVersion]);

    // Ход сборки: событие с сервера и запасной опрос (если связь с сокетом пропала).
    useEffect(() => {
        const socket = getSocket();
        if (!socket?.on) return undefined;

        const onUpdated = (payload = {}) => {
            const updated = payload.catalog;
            if (!updated?._id) return;
            const id = String(updated._id);

            setCatalogs((current) => current.map((item) => (
                item._id === id
                    ? { ...item, build: { ...item.build, ...updated.build }, files: updated.files || item.files }
                    : item
            )));
            setCatalog((current) => (
                current && current._id === id
                    ? { ...current, build: { ...current.build, ...updated.build }, files: updated.files || current.files }
                    : current
            ));
        };

        socket.on("printCatalog:updated", onUpdated);
        return () => socket.off("printCatalog:updated", onUpdated);
    }, []);

    const runningId = running ? catalog._id : "";
    useEffect(() => {
        if (!runningId) return undefined;
        const timer = window.setInterval(() => {
            getPrintCatalog(runningId)
                .then((next) => {
                    if (!mountedRef.current) return;
                    setCatalog((current) => (
                        current && current._id === next._id ? { ...current, build: next.build, files: next.files } : current
                    ));
                    mergeIntoList({ _id: next._id, build: next.build, files: next.files });
                })
                .catch(() => undefined);
        }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [mergeIntoList, runningId]);

    const patch = useCallback((changes) => {
        setDraft((current) => ({ ...current, ...changes }));
        setMessage(null);
    }, []);

    const patchOptions = useCallback((changes) => {
        setDraft((current) => ({ ...current, options: { ...current.options, ...changes } }));
        setMessage(null);
    }, []);

    const patchContacts = useCallback((changes) => {
        setDraft((current) => ({ ...current, contacts: { ...current.contacts, ...changes } }));
        setMessage(null);
    }, []);

    const toggleCategory = useCallback((id) => {
        setDraft((current) => ({
            ...current,
            categoryIds: current.categoryIds.includes(id)
                ? current.categoryIds.filter((item) => item !== id)
                : [...current.categoryIds, id],
        }));
        setMessage(null);
    }, []);

    const addTags = useCallback((tags) => {
        setDraft((current) => ({ ...current, tags: Array.from(new Set([...current.tags, ...tags])).slice(0, 60) }));
        setMessage(null);
    }, []);

    const removeTag = useCallback((tag) => {
        setDraft((current) => ({ ...current, tags: current.tags.filter((item) => item !== tag) }));
        setMessage(null);
    }, []);

    /** Сохранить настройки. Новый каталог создаётся при первом сохранении. */
    const persist = useCallback(async () => {
        const payload = { ...draft, name: draft.name.trim() || draft.title.trim() || "Каталог" };
        const next = catalog
            ? await updatePrintCatalog(catalog._id, payload)
            : await createPrintCatalog(payload);

        applyCatalog(next, true);
        setSelectedId(next._id);
        return next;
    }, [applyCatalog, catalog, draft]);

    const save = useCallback(async () => {
        if (busy) return;
        setBusy("save");
        setMessage(null);
        try {
            await persist();
            if (mountedRef.current) setMessage({ type: "success", text: "Настройки сохранены." });
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось сохранить") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    }, [busy, persist]);

    const build = useCallback(async () => {
        if (busy || running) return;
        if (!hasContent(draft, catalog)) {
            setMessage({ type: "error", text: "Выбери хотя бы одну категорию или тег — иначе в каталоге нечего печатать." });
            return;
        }

        setBusy("build");
        setMessage(null);
        try {
            // Собираем всегда по тому, что на экране: несохранённое сначала сохраняем.
            const current = dirty || !catalog ? await persist() : catalog;
            applyCatalog(await buildPrintCatalog(current._id), false);
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось запустить сборку") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    }, [applyCatalog, busy, catalog, dirty, draft, persist, running]);

    /** Пробник по тому, что сейчас на экране (сохранять не нужно). */
    const refreshPreview = useCallback(async () => {
        if (!hasContent(draft, catalog)) {
            setViewerError("Выбери хотя бы одну категорию или тег — тогда будет что показать.");
            return;
        }

        const seq = previewSeqRef.current + 1;
        previewSeqRef.current = seq;
        setViewerLoading(true);
        setViewerError("");
        try {
            const result = await previewPrintCatalog({ catalogId: catalog?._id || "", draft });
            // Пока собирался этот пробник, могли запросить новый — показываем только последний.
            if (!mountedRef.current || previewSeqRef.current !== seq) return;
            showViewer(result.blob, { kind: "preview", info: result });
        } catch (error) {
            if (!mountedRef.current || previewSeqRef.current !== seq) return;
            setViewerError(await readApiError(error, "Не удалось собрать предпросмотр"));
        } finally {
            if (mountedRef.current && previewSeqRef.current === seq) setViewerLoading(false);
        }
    }, [catalog, draft, showViewer]);

    // Автообновление пробника: через паузу после последней правки.
    const refreshPreviewRef = useRef(refreshPreview);
    refreshPreviewRef.current = refreshPreview;
    const canAutoPreview = autoPreview && selectedId !== "" && !catalogLoading && !running && hasContent(draft, catalog);
    useEffect(() => {
        if (!canAutoPreview) return undefined;
        const timer = window.setTimeout(() => refreshPreviewRef.current(), AUTO_PREVIEW_MS);
        return () => window.clearTimeout(timer);
    }, [canAutoPreview, draftKey, logoVersion, coverVersion]);

    const openFile = useCallback(async (file) => {
        if (!catalog) return;
        previewSeqRef.current += 1;
        setViewerLoading(true);
        setViewerError("");
        try {
            const blob = await fetchPrintCatalogFile(catalog._id, file._id);
            if (!mountedRef.current) return;
            showViewer(blob, { kind: "file", fileId: file._id, info: file });
        } catch (error) {
            if (mountedRef.current) setViewerError(await readApiError(error, "Не удалось открыть PDF"));
        } finally {
            if (mountedRef.current) setViewerLoading(false);
        }
    }, [catalog, showViewer]);

    const fileName = `${(catalog?.name || catalog?.title || "Каталог").replace(/[\\/:*?"<>|]+/g, " ").trim() || "Каталог"}.pdf`;

    const downloadFile = useCallback(async (file) => {
        if (!catalog || busy) return;
        setBusy(`download:${file._id}`);
        setMessage(null);
        try {
            saveAs(await fetchPrintCatalogFile(catalog._id, file._id), fileName);
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось скачать PDF") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    }, [busy, catalog, fileName]);

    const pickImage = useCallback(async (kind, file) => {
        if (busy) return;
        if (file.size > MAX_IMAGE_BYTES) {
            setMessage({ type: "error", text: "Файл слишком большой (больше 20 МБ)." });
            return;
        }

        setBusy(kind);
        setMessage(null);
        try {
            // Картинка привязана к каталогу — новый каталог сначала сохраняем.
            const current = catalog || await persist();
            const next = await uploadPrintCatalogImage(current._id, kind, await readFileAsBase64(file));
            if (!mountedRef.current) return;

            // Сервер меняет только картинку (и режим обложки) — остальные правки на экране не трогаем.
            setCatalog((previous) => (previous ? {
                ...previous,
                hasLogo: next.hasLogo,
                logoUpdatedAt: next.logoUpdatedAt,
                hasCover: next.hasCover,
                coverUpdatedAt: next.coverUpdatedAt,
                options: { ...previous.options, coverMode: next.options.coverMode },
            } : next));
            setDraft((previous) => ({ ...previous, options: { ...previous.options, coverMode: next.options.coverMode } }));
            setMessage({
                type: "success",
                text: kind === "logo"
                    ? "Логотип загружен. В PDF он появится при следующей сборке."
                    : "Фото загружено. Оно встанет на обложку при следующей сборке.",
            });
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось загрузить картинку") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    }, [busy, catalog, persist]);

    const removeImage = useCallback(async (kind) => {
        if (busy || !catalog) return;
        setBusy(kind);
        try {
            const next = await deletePrintCatalogImage(catalog._id, kind);
            if (!mountedRef.current) return;
            setCatalog((previous) => (previous ? {
                ...previous,
                hasLogo: next.hasLogo,
                logoUpdatedAt: next.logoUpdatedAt,
                hasCover: next.hasCover,
                coverUpdatedAt: next.coverUpdatedAt,
                options: { ...previous.options, coverMode: next.options.coverMode },
            } : next));
            setDraft((previous) => ({ ...previous, options: { ...previous.options, coverMode: next.options.coverMode } }));
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось убрать картинку") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    }, [busy, catalog]);

    const remove = useCallback(async () => {
        if (!catalog || busy) return;
        if (!window.confirm(`Удалить каталог «${catalog.name || "Каталог"}»? Настройки и все собранные PDF будут удалены.`)) return;

        setBusy("delete");
        try {
            await deletePrintCatalog(catalog._id);
            if (!mountedRef.current) return;
            setCatalogs((current) => current.filter((item) => item._id !== catalog._id));
            setSelectedId("");
            setCatalog(null);
            setDraft(EMPTY_PRINT_DRAFT);
            showViewer(null, {});
            setMessage(null);
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, "Не удалось удалить каталог") });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    }, [busy, catalog, showViewer]);

    // Закрытие вкладки с несохранёнными правками — браузер переспросит.
    useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const options = draft.options;
    const hasTags = draft.tags.length > 0;
    const selectedCount = categories
        .filter((category) => draft.categoryIds.includes(String(category._id)))
        .reduce((sum, category) => sum + Number(category.productCount || 0), 0);
    const printCount = selectedCount > 0
        ? Math.max(0, Math.min(selectedCount, options.rangeTo || selectedCount) - Math.max(1, options.rangeFrom || 1) + 1)
        : 0;
    const itemsPerPage = options.perPage === 3 ? options.rowsPerPage : options.perPage;
    const pagesEstimate = printCount > 0
        ? Math.ceil(printCount / itemsPerPage) + (options.showCover ? 1 : 0) + (options.showContacts ? 1 : 0)
        : 0;

    const hasReadiness = READY_SWITCHES.some(([key]) => options[key] === true);
    const selectedCategories = categories.filter((category) => draft.categoryIds.includes(String(category._id)));
    const categoriesSummary = draft.categoryIds.length
        ? `${selectedCategories.map((category) => category.name).join(", ") || `выбрано: ${draft.categoryIds.length}`}`
            + (selectedCount > 0 ? ` · около ${selectedCount} тов.` : "")
        : hasTags ? "Не выбраны — берутся все категории" : "Не выбраны — нажми, чтобы выбрать";

    const tagQuery = tagSearch.trim().toLowerCase();
    const foundTags = allTags.filter((item) => (
        !draft.tags.includes(item.tag) && (!tagQuery || item.tag.includes(tagQuery))
    ));

    const buildState = catalog?.build;
    const progress = buildState && buildState.total > 0
        ? Math.max(0, Math.min(1, buildState.progress / buildState.total))
        : 0;

    const renderList = () => (
        <aside className="printCatalogs__list">
            <button
                type="button"
                className="printCatalogs__new"
                onClick={() => openCatalog("new")}
            >
                + Новый каталог
            </button>

            {listLoading && !catalogs.length ? <Loader /> : null}
            {listError ? <div className="printCatalogs__message is-error">{listError}</div> : null}
            {!listLoading && !listError && !catalogs.length ? (
                <p className="printCatalogs__empty">Каталогов пока нет. Создай первый.</p>
            ) : null}

            {catalogs.map((item) => {
                const status = statusInfo(item);
                const last = item.files[0];
                return (
                    <button
                        key={item._id}
                        type="button"
                        className={`printCatalogs__card${item._id === selectedId ? " is-active" : ""}`}
                        onClick={() => openCatalog(item._id)}
                    >
                        <span className="printCatalogs__swatch" style={{ background: item.accentColor || "#1f3b34" }} />
                        <span className="printCatalogs__cardBody">
                            <strong>{item.name || item.title || "Каталог"}</strong>
                            <span>
                                {layoutLabel(item.options)} · {PRICE_LABELS[item.options.priceMode] || ""}
                                {item.categoryIds.length ? ` · категорий: ${item.categoryIds.length}` : ""}
                                {item.tags.length ? ` · тегов: ${item.tags.length}` : ""}
                            </span>
                            <span className={`printCatalogs__status is-${status.tone}`}>
                                {status.text}
                                {last && item.build?.status !== "running"
                                    ? ` · ${last.products} тов., ${last.pages} стр.`
                                    : ""}
                            </span>
                            {last ? (
                                <span>
                                    Собрал: {last.createdByName || "—"} · {formatDateTime(last.createdAt)}
                                </span>
                            ) : (
                                <span>Создал: {item.createdByName || "—"} · {formatDateTime(item.createdAt)}</span>
                            )}
                        </span>
                    </button>
                );
            })}
        </aside>
    );

    const renderFiles = () => (
        <Section title="Готовые PDF" hint="Хранятся пять последних сборок.">
            {catalog?.files.length ? (
                <div className="printCatalogs__files">
                    {catalog.files.map((file, index) => (
                        <div
                            key={file._id}
                            className={`printCatalogs__file${viewer.kind === "file" && viewer.fileId === file._id ? " is-active" : ""}`}
                        >
                            <div className="printCatalogs__fileText">
                                <strong>
                                    {index === 0 ? "Последний" : "Раньше"} · {formatDateTime(file.createdAt)}
                                </strong>
                                <span>
                                    Собрал: {file.createdByName || "—"} · {file.products} товаров · {file.pages} стр.
                                    {formatBytes(file.sizeBytes) ? ` · ${formatBytes(file.sizeBytes)}` : ""}
                                </span>
                                {file.withoutImage || file.withoutPrice ? (
                                    <span className="printCatalogs__status is-warning">
                                        {[
                                            file.withoutImage ? `без фото: ${file.withoutImage}` : "",
                                            file.withoutPrice ? `без цены: ${file.withoutPrice}` : "",
                                        ].filter(Boolean).join(" · ")}
                                    </span>
                                ) : null}
                            </div>
                            <div className="printCatalogs__fileActions">
                                <button type="button" onClick={() => openFile(file)}>Смотреть</button>
                                <button
                                    type="button"
                                    disabled={!!busy}
                                    onClick={() => downloadFile(file)}
                                >
                                    {busy === `download:${file._id}` ? "Скачивание…" : "Скачать"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="printCatalogs__hint">Пока ни одного. Нажми «Собрать PDF» внизу.</p>
            )}
        </Section>
    );

    const renderForm = () => (
        <div className="printCatalogs__form">
            <Section title={catalog ? "Каталог" : "Новый каталог"}>
                <Field
                    label="Название каталога (для себя, в журнал не попадает)"
                    value={draft.name}
                    onChange={(name) => patch({ name })}
                    placeholder="Например: Памятники, весна 2026"
                    maxLength={120}
                    disabled={locked}
                />
                {catalog ? (
                    <p className="printCatalogs__hint">
                        Создал: {catalog.createdByName || "—"} · {formatDateTime(catalog.createdAt)}
                        {catalog.updatedByName ? ` · Изменил: ${catalog.updatedByName} · ${formatDateTime(catalog.updatedAt)}` : ""}
                    </p>
                ) : null}
            </Section>

            {catalog ? renderFiles() : null}

            <Section
                title="Что войдёт"
                hint="Категории, теги и готовность товара — вместе они решают, какие товары попадут в каталог."
            >
                <Collapse
                    title="Категории"
                    summary={categoriesSummary}
                    open={categoriesOpen}
                    onToggle={() => setCategoriesOpen((open) => !open)}
                >
                    <p className="printCatalogs__hint">
                        Отметь категории — их товары попадут в каталог, каждая своим разделом.
                    </p>
                    {categories.length ? (
                        <div className="printCatalogs__chips">
                            {categories.map((category) => {
                                const id = String(category._id);
                                const active = draft.categoryIds.includes(id);
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`printCatalogs__chip${active ? " is-active" : ""}`}
                                        disabled={locked}
                                        onClick={() => toggleCategory(id)}
                                    >
                                        {category.name}
                                        <em>{Number(category.productCount || 0)}</em>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="printCatalogs__hint">Категории загружаются…</p>
                    )}

                    <label className="printCatalogs__switch">
                        <input
                            type="checkbox"
                            checked={options.onlyVisible}
                            disabled={locked}
                            onChange={(event) => patchOptions({ onlyVisible: event.target.checked })}
                        />
                        <span>Только товары, видимые на сайте</span>
                    </label>
                </Collapse>

                <Collapse
                    title="Теги — дополнительное условие"
                    summary={hasTags ? draft.tags.join(", ") : "Не выбраны — берутся товары с любыми тегами"}
                    open={tagsOpen}
                    onToggle={() => setTagsOpen((open) => !open)}
                >
                    <p className="printCatalogs__hint">
                        В каталог попадут только товары с выбранными тегами. Если категории не отмечены —
                        все товары с такими тегами.
                    </p>
                    {hasTags ? (
                        <div className="printCatalogs__chips">
                            {draft.tags.map((tag) => (
                                <button
                                    key={tag}
                                    type="button"
                                    className="printCatalogs__chip is-active"
                                    disabled={locked}
                                    title="Убрать тег"
                                    onClick={() => removeTag(tag)}
                                >
                                    {tag}
                                    <em>×</em>
                                </button>
                            ))}
                        </div>
                    ) : null}
                    {draft.tags.length > 1 ? (
                        <Choice
                            options={TAG_MATCHES}
                            value={options.tagMatch}
                            onChange={(tagMatch) => patchOptions({ tagMatch })}
                            disabled={locked}
                        />
                    ) : null}
                    <div className="printCatalogs__tagSearch">
                        <input
                            type="text"
                            value={tagSearch}
                            placeholder="Найти тег: роз, крест, ангел…"
                            disabled={locked}
                            onChange={(event) => setTagSearch(event.target.value)}
                        />
                        {tagQuery && foundTags.length > 1 ? (
                            <button
                                type="button"
                                disabled={locked}
                                onClick={() => {
                                    addTags(foundTags.map((item) => item.tag));
                                    setTagSearch("");
                                }}
                            >
                                Добавить все найденные ({foundTags.length})
                            </button>
                        ) : null}
                    </div>
                    {foundTags.length ? (
                        <div className="printCatalogs__chips is-compact">
                            {foundTags.slice(0, TAG_SUGGESTIONS).map((item) => (
                                <button
                                    key={item.tag}
                                    type="button"
                                    className="printCatalogs__chip"
                                    disabled={locked}
                                    onClick={() => addTags([item.tag])}
                                >
                                    {item.tag}
                                    <em>{item.count}</em>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="printCatalogs__hint">
                            {allTags.length ? "Таких тегов нет." : "Теги загружаются… Если список пуст — у товаров ещё нет тегов."}
                        </p>
                    )}
                    {foundTags.length > TAG_SUGGESTIONS ? (
                        <p className="printCatalogs__hint">Показаны самые частые. Остальные ищи по слову.</p>
                    ) : null}
                </Collapse>

                <div className="printCatalogs__label">Готовность товара</div>
                <div className="printCatalogs__switches is-single">
                    {READY_SWITCHES.map(([key, label]) => (
                        <label key={key} className="printCatalogs__switch">
                            <input
                                type="checkbox"
                                checked={options[key] === true}
                                disabled={locked}
                                onChange={(event) => patchOptions({ [key]: event.target.checked })}
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                </div>
                <p className="printCatalogs__hint">
                    Включи все три — в каталог пойдут только полностью готовые товары. Сколько их набралось, покажет предпросмотр.
                </p>

                <div className="printCatalogs__label">Печатать товары по порядку</div>
                <div className="printCatalogs__range">
                    <span>с</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={options.rangeFrom ? String(options.rangeFrom) : ""}
                        placeholder="1"
                        disabled={locked}
                        onChange={(event) => patchOptions({ rangeFrom: toPosition(event.target.value) })}
                    />
                    <span>по</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={options.rangeTo ? String(options.rangeTo) : ""}
                        placeholder="конец"
                        disabled={locked}
                        onChange={(event) => patchOptions({ rangeTo: toPosition(event.target.value) })}
                    />
                    {options.rangeFrom || options.rangeTo ? (
                        <button type="button" disabled={locked} onClick={() => patchOptions({ rangeFrom: 0, rangeTo: 0 })}>
                            Все
                        </button>
                    ) : null}
                </div>
                <p className="printCatalogs__hint">
                    Пусто — весь каталог. Для пробы поставь, например, с 45 по 64: соберутся только эти 20 штук,
                    а номера на карточках останутся как в полном каталоге.
                    {hasTags || hasReadiness
                        ? " С тегами и условиями готовности точное число товаров покажет предпросмотр."
                        : printCount > 0 ? ` Сейчас в печать: около ${printCount} тов., страниц: около ${pagesEstimate}.` : ""}
                </p>
            </Section>

            <Section title="Вид страницы">
                <div className="printCatalogs__label">Оформление</div>
                <Choice options={DESIGNS} value={options.design} onChange={(design) => patchOptions({ design })} disabled={locked} />
                {options.design === "dark" ? (
                    <p className="printCatalogs__hint">
                        В тёмном оформлении свои цвета — выбранный ниже цвет плашек не действует. На печать уходит много краски.
                    </p>
                ) : null}

                <div className="printCatalogs__label">Карточек на странице</div>
                <Choice options={PER_PAGE} value={options.perPage} onChange={(perPage) => patchOptions({ perPage })} disabled={locked} />
                {options.perPage === 3 ? (
                    <>
                        <div className="printCatalogs__label">Товаров на странице по высоте</div>
                        <Choice
                            options={ROWS_PER_PAGE}
                            value={options.rowsPerPage}
                            onChange={(rowsPerPage) => patchOptions({ rowsPerPage })}
                            disabled={locked}
                        />
                        <p className="printCatalogs__hint">
                            Один товар на всю ширину листа и три его первых фото (четвёртое не берётся).
                            При 1 и 2 на странице главное фото крупное, остальные рядом. Файл выйдет тяжелее: фотографий втрое больше.
                        </p>
                    </>
                ) : null}

                <div className="printCatalogs__label">Фото товара</div>
                <Choice options={IMAGE_CROPS} value={options.imageCrop} onChange={(imageCrop) => patchOptions({ imageCrop })} disabled={locked} />

                <div className="printCatalogs__label">Какую цену печатать</div>
                <Choice options={PRICE_MODES} value={options.priceMode} onChange={(priceMode) => patchOptions({ priceMode })} disabled={locked} />

                <div className="printCatalogs__label">Номер на карточке</div>
                <Choice options={NUMBERINGS} value={options.numbering} onChange={(numbering) => patchOptions({ numbering })} disabled={locked} />

                <div className="printCatalogs__label">Порядок товаров</div>
                <Choice options={SORTS} value={options.sort} onChange={(sort) => patchOptions({ sort })} disabled={locked} />

                <div className="printCatalogs__switches">
                    {SWITCHES.map(([key, label]) => (
                        <label key={key} className="printCatalogs__switch">
                            <input
                                type="checkbox"
                                checked={options[key] !== false}
                                disabled={locked}
                                onChange={(event) => patchOptions({ [key]: event.target.checked })}
                            />
                            <span>{label}</span>
                        </label>
                    ))}
                </div>

                <div className="printCatalogs__label">Цвет плашек и заголовков</div>
                <div className="printCatalogs__colors">
                    {ACCENT_COLORS.map((color) => (
                        <button
                            key={color}
                            type="button"
                            className={draft.accentColor === color ? "is-active" : ""}
                            style={{ background: color }}
                            disabled={locked}
                            title={color}
                            aria-label={`Цвет ${color}`}
                            onClick={() => patch({ accentColor: color })}
                        />
                    ))}
                    <label className="printCatalogs__colorCustom" title="Свой цвет">
                        <input
                            type="color"
                            value={draft.accentColor}
                            disabled={locked}
                            onChange={(event) => patch({ accentColor: event.target.value.toLowerCase() })}
                        />
                        <span>Свой</span>
                    </label>
                </div>
            </Section>

            {options.showCover ? (
                <Section
                    title="Обложка"
                    hint="Широкое фото (16:9 или ряд памятников) встаёт от края до края листа целиком, без обрезки. Квадратное и высокое — в рамке по центру."
                >
                    <div className="printCatalogs__label">Что на первой странице</div>
                    <Choice options={COVER_MODES} value={options.coverMode} onChange={(coverMode) => patchOptions({ coverMode })} disabled={locked} />

                    {options.coverMode === "product" ? (
                        <Field
                            label="Артикул товара для обложки"
                            value={options.coverArticle}
                            onChange={(coverArticle) => patchOptions({ coverArticle: coverArticle.replace(/\s/g, "") })}
                            placeholder="Например: 100915"
                            maxLength={24}
                            disabled={locked}
                        />
                    ) : null}

                    {options.coverMode === "photo" ? (
                        <ImageUpload
                            wide
                            url={coverUrl}
                            has={!!catalog?.hasCover}
                            busy={busy === "cover"}
                            disabled={locked}
                            emptyText="Фото не загружено"
                            onPick={(file) => pickImage("cover", file)}
                            onRemove={() => removeImage("cover")}
                        />
                    ) : null}
                </Section>
            ) : null}

            <Section
                title="Защита от копирования"
                hint="Знак владельца на фото и листах, чтобы каталог и фотографии нельзя было выдать за свои."
            >
                <div className="printCatalogs__label">Знак на каждом фото товара</div>
                <Choice options={PHOTO_MARKS} value={options.photoMark} onChange={(photoMark) => patchOptions({ photoMark })} disabled={locked} />
                <p className="printCatalogs__hint">
                    Знак вплавляется в саму фотографию — убрать его из PDF или вытащить фото без знака нельзя.
                </p>

                <label className="printCatalogs__switch">
                    <input
                        type="checkbox"
                        checked={options.pageMark === true}
                        disabled={locked}
                        onChange={(event) => patchOptions({ pageMark: event.target.checked })}
                    />
                    <span>Большой знак поверх каждого листа</span>
                </label>

                {options.photoMark !== "none" || options.pageMark ? (
                    <>
                        <div className="printCatalogs__label">Что служит знаком</div>
                        <Choice options={MARK_SOURCES} value={options.markSource} onChange={(markSource) => patchOptions({ markSource })} disabled={locked} />
                        {options.markSource === "text" ? (
                            <Field
                                label="Текст знака (пусто — название фирмы)"
                                value={options.markText}
                                onChange={(markText) => patchOptions({ markText })}
                                placeholder={draft.companyName || "Например: alotb.ru"}
                                maxLength={60}
                                disabled={locked}
                            />
                        ) : (
                            <p className="printCatalogs__hint">
                                Берётся логотип из блока «Фирма и контакты». Если он не загружен — встанет название.
                            </p>
                        )}
                        <div className="printCatalogs__label">Насколько заметен</div>
                        <Choice options={MARK_STRENGTHS} value={options.markStrength} onChange={(markStrength) => patchOptions({ markStrength })} disabled={locked} />
                    </>
                ) : null}

                <label className="printCatalogs__switch">
                    <input
                        type="checkbox"
                        checked={options.showSign !== false}
                        disabled={locked}
                        onChange={(event) => patchOptions({ showSign: event.target.checked })}
                    />
                    <span>Подпись внизу каждой страницы</span>
                </label>
                {options.showSign !== false ? (
                    <Field
                        label="Текст подписи (пусто — фирма, телефон, сайт)"
                        value={options.signText}
                        onChange={(signText) => patchOptions({ signText })}
                        placeholder="Например: Каменный двор · +7 900 000-00-00"
                        maxLength={120}
                        disabled={locked}
                    />
                ) : null}
            </Section>

            <Section title="Тексты">
                <div className="printCatalogs__grid2">
                    <Field label="Заголовок" value={draft.title} onChange={(title) => patch({ title })} maxLength={60} disabled={locked} />
                    <Field label="Подзаголовок" value={draft.subtitle} onChange={(subtitle) => patch({ subtitle })} maxLength={60} disabled={locked} />
                    <Field
                        label="Девиз (рукописным шрифтом)"
                        value={draft.motto}
                        onChange={(motto) => patch({ motto })}
                        placeholder="Память в камне"
                        maxLength={40}
                        disabled={locked}
                    />
                    <Field label="Выпуск" value={draft.edition} onChange={(edition) => patch({ edition })} placeholder="Выпуск 2026" maxLength={40} disabled={locked} />
                </div>
                <Field
                    label="Текст на странице контактов"
                    value={draft.backText}
                    onChange={(backText) => patch({ backText })}
                    multiline
                    maxLength={400}
                    disabled={locked}
                />
            </Section>

            <Section title="Фирма и контакты">
                <Field label="Название фирмы" value={draft.companyName} onChange={(companyName) => patch({ companyName })} maxLength={80} disabled={locked} />

                <div className="printCatalogs__label">Логотип вместо названия (на обложке и в контактах)</div>
                <ImageUpload
                    url={logoUrl}
                    has={!!catalog?.hasLogo}
                    busy={busy === "logo"}
                    disabled={locked}
                    emptyText="Логотипа нет"
                    onPick={(file) => pickImage("logo", file)}
                    onRemove={() => removeImage("logo")}
                />
                <p className="printCatalogs__hint">Лучше всего PNG с прозрачным фоном, вытянутый по ширине.</p>

                <div className="printCatalogs__grid2">
                    <Field label="Телефон" value={draft.contacts.phone} onChange={(phone) => patchContacts({ phone })} maxLength={60} disabled={locked} />
                    <Field label="Сайт" value={draft.contacts.site} onChange={(site) => patchContacts({ site })} maxLength={80} disabled={locked} />
                    <Field label="Адрес" value={draft.contacts.address} onChange={(address) => patchContacts({ address })} maxLength={160} disabled={locked} />
                    <Field label="Режим работы" value={draft.contacts.hours} onChange={(hours) => patchContacts({ hours })} maxLength={80} disabled={locked} />
                </div>
                <Field
                    label="Ссылка для QR на странице контактов"
                    value={draft.url}
                    onChange={(url) => patch({ url })}
                    placeholder="https://alotb.ru"
                    maxLength={300}
                    disabled={locked}
                />
                <Field
                    label="Начало ссылки в QR товара (в конец добавляется id товара)"
                    value={options.qrBaseUrl}
                    onChange={(qrBaseUrl) => patchOptions({ qrBaseUrl })}
                    placeholder="https://alotb.ru/productDetails/"
                    maxLength={300}
                    disabled={locked}
                />
            </Section>

            {catalog ? (
                <button type="button" className="printCatalogs__delete" disabled={locked} onClick={remove}>
                    {busy === "delete" ? "Удаление…" : "Удалить каталог"}
                </button>
            ) : null}
        </div>
    );

    const renderActions = () => (
        <div className="printCatalogs__actions">
            {running ? (
                <div className="printCatalogs__progress">
                    <span>
                        {buildState?.message || "Собираем каталог"}
                        {buildState && buildState.total > 0 ? ` · ${buildState.progress} из ${buildState.total}` : ""}
                    </span>
                    <div><i style={{ width: `${Math.round(progress * 100)}%` }} /></div>
                </div>
            ) : null}

            {message ? (
                <div className={`printCatalogs__message is-${message.type}`}>{message.text}</div>
            ) : !running && buildState?.status === "error" ? (
                <div className="printCatalogs__message is-error">{buildState.message || "Сборка не удалась"}</div>
            ) : !running && buildState?.status === "done" && !dirty ? (
                <div className="printCatalogs__message is-success">{buildState.message}</div>
            ) : null}

            <div className="printCatalogs__buttons">
                <button type="button" disabled={locked || (!dirty && !!catalog)} onClick={save}>
                    {busy === "save" ? "Сохранение…" : dirty || !catalog ? "Сохранить" : "Сохранено"}
                </button>
                <button type="button" disabled={viewerLoading || running} onClick={refreshPreview}>
                    Предпросмотр
                </button>
                <button type="button" className="is-primary" disabled={locked} onClick={build}>
                    {running || busy === "build" ? "Собирается…" : catalog?.files.length ? "Собрать заново" : "Собрать PDF"}
                </button>
            </div>
        </div>
    );

    const renderViewer = () => (
        <div className="printCatalogs__viewer">
            <div className="printCatalogs__viewerHead">
                <div>
                    <strong>
                        {viewer.kind === "file" ? "Собранный PDF" : "Предпросмотр"}
                    </strong>
                    <span>
                        {viewer.kind === "file" && viewer.info
                            ? `${formatDateTime(viewer.info.createdAt)} · собрал: ${viewer.info.createdByName || "—"} · ${viewer.info.products} тов., ${viewer.info.pages} стр.`
                            : viewer.kind === "preview" && viewer.info
                                ? `Обложка, две страницы и контакты. В полном каталоге: ${viewer.info.products} тов., ${viewer.info.pages} стр.${viewer.info.notes ? ` (${viewer.info.notes})` : ""}`
                                : "Обложка, две страницы товаров и контакты — по тому, что сейчас в настройках."}
                    </span>
                </div>
                <div className="printCatalogs__viewerTools">
                    <label className="printCatalogs__switch">
                        <input
                            type="checkbox"
                            checked={autoPreview}
                            onChange={(event) => setAutoPreview(event.target.checked)}
                        />
                        <span>Обновлять сам</span>
                    </label>
                    <button type="button" disabled={viewerLoading || running} onClick={refreshPreview}>
                        {viewerLoading ? "Собираем…" : "Обновить"}
                    </button>
                </div>
            </div>

            {viewerError ? <div className="printCatalogs__message is-error">{viewerError}</div> : null}

            <div className={`printCatalogs__frame${viewerLoading ? " is-loading" : ""}`}>
                {viewer.url ? (
                    <iframe title="Просмотр каталога" src={`${viewer.url}#view=FitH`} />
                ) : (
                    <div className="printCatalogs__frameEmpty">
                        {viewerLoading
                            ? "Собираем предпросмотр…"
                            : "Нажми «Предпросмотр» — здесь появятся обложка и первые страницы. Или открой любой собранный PDF кнопкой «Смотреть»."}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Layout sidebar>
            <div className="printCatalogs">
                <header className="printCatalogs__header">
                    <div>
                        <div className="printCatalogs__adminMark">ADMIN</div>
                        <h1>Каталоги под печать</h1>
                        <p>
                            Журнал в PDF: обложка, страницы с фото, ценами и QR-кодами, страница контактов.
                            Настрой один раз и пересобирай, когда меняются цены.
                        </p>
                    </div>
                    <div className="printCatalogs__headerActions">
                        <Link to="/admin/print-signs">Вывески</Link>
                        <Link to="/admin/catalog-review">Каталог форм</Link>
                        <Link to="/containerProductPage/product-seo-price">Работы + теги</Link>
                        <button type="button" onClick={() => loadList()}>Обновить список</button>
                    </div>
                </header>

                <div className="printCatalogs__body">
                    {renderList()}

                    {selectedId === "" ? (
                        <div className="printCatalogs__placeholder">
                            Выбери каталог слева или создай новый.
                        </div>
                    ) : catalogLoading ? (
                        <div className="printCatalogs__placeholder"><Loader /></div>
                    ) : (
                        <>
                            <div className="printCatalogs__editor">
                                {renderForm()}
                                {renderActions()}
                            </div>
                            {renderViewer()}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default PrintCatalogsPage;
